import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase";
import type { Database } from "@shared/database.types";

type Profile = Database["public"]["Tables"]["profiles"]["Row"];
type AppRole = Database["public"]["Enums"]["app_role"];

interface AuthContextValue {
  session: Session | null;
  user: User | null;
  profile: Profile | null;
  roles: AppRole[];
  agencyId: string | null;
  loading: boolean;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  isClient: boolean;
  /** Verifica se il caller può accedere al modulo UI (tasks/clients/finance/ecc). */
  canAccess: (module: string) => boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [roles, setRoles] = useState<AppRole[]>([]);
  const [loading, setLoading] = useState(true);

  // ============================================================
  // PATTERN CORRETTO Supabase auth:
  // 1. onAuthStateChange aggiorna lo stato sincrono.
  // 2. Le query async (profile, roles) vengono fatte in setTimeout(0)
  //    per evitare deadlock interno al lock auth.
  // ============================================================

  useEffect(() => {
    let active = true;

    // Subscription PRIMA del get session, così catturiamo eventi
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, newSession) => {
        if (!active) return;

        setSession(newSession);
        setUser(newSession?.user ?? null);

        if (newSession?.user) {
          // Defer fetch per evitare deadlock
          setTimeout(() => {
            if (active) {
              void loadProfileAndRoles(newSession.user.id);
            }
          }, 0);
        } else {
          setProfile(null);
          setRoles([]);
          setLoading(false);
        }
      }
    );

    // Recupera la sessione iniziale
    supabase.auth.getSession().then(({ data: { session: initial } }) => {
      if (!active) return;

      setSession(initial);
      setUser(initial?.user ?? null);

      if (initial?.user) {
        setTimeout(() => {
          if (active) void loadProfileAndRoles(initial.user.id);
        }, 0);
      } else {
        setLoading(false);
      }
    });

    return () => {
      active = false;
      subscription.unsubscribe();
    };
  }, []);

  async function loadProfileAndRoles(userId: string) {
    try {
      const [{ data: profileData }, { data: rolesData }] = await Promise.all([
        supabase
          .from("profiles")
          .select("*")
          .eq("user_id", userId)
          .maybeSingle(),
        supabase
          .from("user_roles")
          .select("role")
          .eq("user_id", userId),
      ]);

      setProfile(profileData ?? null);
      setRoles((rolesData ?? []).map((r) => r.role));
    } catch (err) {
      console.error("[AuthContext] load profile/roles failed:", err);
    } finally {
      setLoading(false);
    }
  }

  async function signOut() {
    await supabase.auth.signOut();
    setProfile(null);
    setRoles([]);
  }

  const isAdmin =
    roles.includes("super_admin") || roles.includes("team_member");
  const isSuperAdmin = roles.includes("super_admin");
  const isClient =
    roles.includes("client_admin") || roles.includes("client_viewer");

  // Permission check basato su profile.team_permissions:
  // - super_admin → sempre true
  // - team_member con permissions === null → sempre true (legacy)
  // - team_member con permissions array → solo i moduli inclusi
  // - client_* → fuori scope (gestiti diversamente lato portale)
  function canAccess(module: string): boolean {
    if (isSuperAdmin) return true;
    if (!isAdmin) return false;
    const perms = profile?.team_permissions;
    if (perms === null || perms === undefined) return true;
    return perms.includes(module);
  }

  const value: AuthContextValue = {
    session,
    user,
    profile,
    roles,
    agencyId: profile?.agency_id ?? null,
    loading,
    isAdmin,
    isSuperAdmin,
    isClient,
    canAccess,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth deve essere usato dentro <AuthProvider>");
  }
  return ctx;
}
